import React from 'react';
import moment from 'moment';
import { MemoizedHbtDataTableCell } from 'Feature/V2/Shared/MemoizedHbtDataTableCell';
import { InsuredApprovedLenderName } from '../../../types';
import HBTTableCell from '../../../../HbtPortfolioManagementTable/components/HBTTableCell';
import { TranslationType } from '../types';
import StatusPill from 'Feature/V2/HbtPortfolioManagement/components/StatusPill';
import { statusMapper } from 'Feature/V2/HbtPortfolioManagement/components/HbtPortfolioManagementRequestOverview/components/PortfolioCard/utils/PortfolioStatusMapper';
import LoanStatusPill from 'Feature/V2/HbtPortfolioManagement/components/HbtPortfolioManagementRequestOverview/components/HbtPortfolioLoansInventoryTable/LoanStatusPill';
import { loanStatusMapping } from 'Feature/V2/HbtPortfolioManagement/components/HbtPortfolioManagementRequestOverview/components/HbtPortfolioLoansInventoryTable/utils/LoanStatusMapping';
import { Tooltip } from '@mui/material';
import styles from '../styles.module.scss';
import { Borrower } from '../types';
import { RemarkDetail } from 'Feature/V2/HbtPortfolioManagement/components/HbtPortfolioManagementRequestOverview/components/HbtPortfolioLoansInventoryTable/types';
import i18n from 'i18next';
import { isRemarkOther } from '../../../../../../Shared_Portfolio/utils/remarkOther';

export const loanRecordStatusMapping = (t: TranslationType) => {
  const mapping = {
    '01': t('Received'),
    '02': t('Passed'),
    '03': t('Failed'),
    '04': t('Review-Required'),
    '09': t('Deleted'),
    Total: t('Total')
  };
  return mapping;
};

const getLoanRecordStatusLabel = (
  status: keyof typeof loanRecordStatusMapping,
  t: TranslationType
) => {
  return loanRecordStatusMapping(t)[status];
};

const getLoanStatusLabel = (status: keyof typeof loanStatusMapping, t: TranslationType) => {
  return loanStatusMapping(t)[status];
};

const getInsuredApprovedlenderName = (data: InsuredApprovedLenderName, language: string) => {
  return language === 'en' ? data?.englishName : data?.frenchName;
};

const getBorrowerName = (data: Borrower) => {
  return `${data?.lastName ? data?.lastName + ',' : ''} ${data?.firstName ?? ''}`;
};

export const createInsuredLenderNameRenderer =
  (language: string) =>
  ({ cell }: { cell: any }) => {
    const data = cell.getValue();
    const displayName = getInsuredApprovedlenderName(data, language);
    return (
      <MemoizedHbtDataTableCell
        dataValue={displayName || ''}
        cellContent={<HBTTableCell title={displayName}>{displayName}</HBTTableCell>}
      />
    );
  };

export const borrowerNameRenderer =
  () =>
  ({ cell }: { cell: any }) => {
    const data = cell.getValue();
    const displayName = getBorrowerName(data);
    return (
      <MemoizedHbtDataTableCell
        dataValue={displayName || ''}
        cellContent={<HBTTableCell title={displayName}>{displayName}</HBTTableCell>}
      />
    );
  };

export const renderFileReceivedTimeStampCell = ({ cell }: { cell: any }) => {
  const value = cell.getValue();
  const displayValue = value ? moment.utc(value).format('YYYY-MM-DD') : '';
  return (
    <MemoizedHbtDataTableCell
      dataValue={displayValue as string}
      cellContent={<HBTTableCell title={String(displayValue)}>{String(displayValue)}</HBTTableCell>}
    />
  );
};

export const renderDefaultCell = ({ cell }: { cell: any }) => {
  const value = cell.getValue() || '';
  return (
    <MemoizedHbtDataTableCell
      dataValue={String(value)}
      cellContent={<HBTTableCell title={String(value)}>{String(value)}</HBTTableCell>}
    />
  );
};

export const renderEpipProcessingStatusCell =
  (t: TranslationType) =>
  ({ cell, row }: { cell: any; row: any }) => {
    const data = cell.getValue() || '';
    const { getLabel } = statusMapper(t);
    return (
      <MemoizedHbtDataTableCell
        dataValue={data as string}
        cellContent={<StatusPill status={String(data)} label={getLabel(String(data))} />}
      />
    );
  };

export const renderLoanStatusCell =
  (t: TranslationType) =>
  ({ cell, row }: { cell: any; row: any }) => {
    const data = cell.getValue() || '';
    return (
      <MemoizedHbtDataTableCell
        dataValue={data as string}
        cellContent={
          <LoanStatusPill
            status={String(data)}
            label={getLoanStatusLabel(String(data) as keyof typeof loanStatusMapping, t)}
          />
        }
      />
    );
  };

export const renderLoanRecordStatusCell =
  (t: TranslationType) =>
  ({ cell, row }: { cell: any; row: any }) => {
    const data = cell.getValue() || '';
    return (
      <MemoizedHbtDataTableCell
        dataValue={data as string}
        cellContent={
          <LoanStatusPill
            status={String(data)}
            label={getLoanRecordStatusLabel(
              String(data) as keyof typeof loanRecordStatusMapping,
              t
            )}
          />
        }
      />
    );
  };

export const renderCmhcAccountNumberCell =
  (t: TranslationType) =>
  ({ cell, row }: { cell: any; row: any }) => {
    const value = cell.getValue() || '';
    const isReducedDataSet = row.original?.reducedDatasetIndicator;
    const showReducedaDataSet = Boolean(value) && isReducedDataSet;
    return (
      <MemoizedHbtDataTableCell
        dataValue={String(value)}
        cellContent={
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              whiteSpace: 'noWrap',
              overflow: 'hidden',
              textOverflow: 'ellipses'
            }}
          >
            <HBTTableCell title={String(value)}>{String(value)}</HBTTableCell>
            {showReducedaDataSet && (
              <Tooltip
                title={t('Reduced-Dataset-Tool-Tip-Text')}
                placement="right"
                arrow
                slotProps={{
                  tooltip: {
                    className: styles.loansInventoryToolTip
                  },
                  arrow: {
                    className: styles.tooltipArrow
                  }
                }}
              >
                <div
                  style={{
                    marginLeft: '8px'
                  }}
                >
                  (R)
                </div>
              </Tooltip>
            )}
          </div>
        }
      />
    );
  };

export const renderDateCell = ({ cell }: { cell: any }) => {
  const value = cell.getValue();
  return (
    <MemoizedHbtDataTableCell
      dataValue={value as string}
      cellContent={<HBTTableCell title={String(value)}>{String(value)}</HBTTableCell>}
    />
  );
};

const getRemarkValue = (remark: RemarkDetail) => {
  return isRemarkOther({ remarkID: remark.remarkId, remarkMessage: '' })
    ? remark.remarkText
    : i18n.language === 'en'
      ? remark.rationaleEnglish
      : remark.rationaleFrench;
};
export const renderRemark = ({ cell, row }: { cell: any; row: any }) => {
  const value = cell.getValue() ? getRemarkValue(cell.getValue()) : '';
  return (
    <MemoizedHbtDataTableCell
      dataValue={String(value)}
      cellContent={<HBTTableCell title={String(value)}>{String(value)}</HBTTableCell>}
    />
  );
};
